import { createStore } from "zustand";
import type { Theme } from "@/lib/client/utils/theme/types";
import { DEFAULT_THEME } from "@/lib/client/utils/theme/types";
import type { GoTo } from "@deskpro-product/messenger-api-schemas/messenger-init/types";
import type {
  MessengerConfig,
  UrlCacheableConfig,
} from "@deskpro-product/messenger-api-schemas/messenger/config";
import type { BasePath } from "../../../[locale]/lib/types/routeTypes";

type ConversationId = string;
type SdkMessage = { type: "text"; text: string };
export type SdkConversationMessages = Record<ConversationId, SdkMessage[]>;
export type LauncherStoreState = {
  /** Default: false. Set to true in LauncherContext once init() has run. Avoids redirect() in InitLocale running too soon */
  isInitialized: boolean;
  showLauncherButton: boolean;
  isOpen: boolean;
  isOpenAnimating: boolean;
  isExpanded: boolean;
  theme: Theme;
  config?: MessengerConfig;
  goTo?: GoTo;
  isFullscreen: boolean;
  platform: NonNullable<UrlCacheableConfig["platform"]>;
  host: string;
  authStatus?: "error" | "ok";
  sdkConversationMessages?: SdkConversationMessages;
  tenantId?: string;
  messengerId?: string;
  basePath: BasePath;
  pushDeviceToken?: string;
  mediaBaseUrl?: string;
  overrideLocale?: string;
  isVisible?: boolean;
};

export type LauncherStoreActions = {
  setIsInitialized: (isInitialized: boolean) => void;
  setShowLauncherButton: (show: boolean) => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsOpenAnimating: (isOpenAnimating: boolean) => void;
  setIsExpanded: (isExpanded: boolean) => void;
  setTheme: (theme: Theme) => void;
  setConfig: (config: MessengerConfig) => void;
  setGoTo: (goto?: GoTo) => void;
  setIsFullscreen: (isFullscreen: boolean) => void;
  setPlatform: (platform: NonNullable<UrlCacheableConfig["platform"]>) => void;
  setHost: (host: string) => void;
  setAuthStatus: (status?: "error" | "ok") => void;
  setSdkConversationMessages: (messages: SdkConversationMessages) => void;
  setTenantId: (tenantId: string) => void;
  setMessengerId: (messengerId: string) => void;
  setBasePath: (basePath: BasePath) => void;
  setPushDeviceToken: (pushDeviceToken?: string) => void;
  setMediaBaseUrl: (mediaBaseUrl?: string) => void;
  setOverrideLocale: (overrideLocale?: string) => void;
  setIsVisible: (isVisible?: boolean) => void;
};

export type LauncherStore = LauncherStoreState & LauncherStoreActions;

const defaultInitState: LauncherStoreState = {
  isInitialized: false,
  showLauncherButton: true,
  isOpen: false,
  isOpenAnimating: false,
  isExpanded: false,
  isVisible: false,
  theme: DEFAULT_THEME,
  config: undefined,
  goTo: undefined,
  host: "",
  isFullscreen: true,
  platform: "WEB",
  authStatus: undefined,
  basePath: "/tenant-id/messenger-id/d/en-US",
  mediaBaseUrl: undefined,
};

export const createLauncherStore = (initState: LauncherStoreState = defaultInitState) =>
  createStore<LauncherStore>()((set) => ({
    ...initState,
    setIsInitialized: (isInitialized: boolean) => set(() => ({ isInitialized })),
    setShowLauncherButton: (show: boolean) => set(() => ({ showLauncherButton: show })),
    setIsOpen: (open: boolean) => set(() => ({ isOpen: open })),
    setIsOpenAnimating: (animating: boolean) => set(() => ({ isOpenAnimating: animating })),
    setIsExpanded: (isExpanded: boolean) => set(() => ({ isExpanded })),
    setTheme: (theme: Theme) => set(() => ({ theme })),
    setConfig: (config: MessengerConfig) => set(() => ({ config })),
    setGoTo: (goto?: GoTo) => set(() => ({ goTo: goto })),
    setIsFullscreen: (isFullscreen: boolean) => set(() => ({ isFullscreen })),
    setPlatform: (platform: NonNullable<UrlCacheableConfig["platform"]>) =>
      set(() => ({ platform })),
    setHost: (host: string) => set(() => ({ host })),
    setAuthStatus: (authStatus?: "error" | "ok") => set(() => ({ authStatus })),
    setSdkConversationMessages: (sdkConversationMessages: SdkConversationMessages) =>
      set(() => ({ sdkConversationMessages })),
    setTenantId: (tenantId: string) => set(() => ({ tenantId })),
    setMessengerId: (messengerId: string) => set(() => ({ messengerId })),
    setBasePath: (basePath: BasePath) => set(() => ({ basePath })),
    setPushDeviceToken: (pushDeviceToken?: string) => set(() => ({ pushDeviceToken })),
    setMediaBaseUrl: (mediaBaseUrl?: string) => set(() => ({ mediaBaseUrl })),
    setOverrideLocale: (overrideLocale?: string) => set(() => ({ overrideLocale })),
    setIsVisible: (isVisible?: boolean) => set(() => ({ isVisible })),
  }));
